import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { Provider, ProviderType, TrackingEvent } from '../types';
import { getOrCreateDeviceId } from '../utils/deviceIdStorage';

/** RTB House 客户端业务事件出口总开关；当前固定关闭，仅保留 Provider 初始化能力。 */
const RTB_HOUSE_BUSINESS_EVENTS_ENABLED = false;

interface RtbHouseConfig {
  trackingId: string;
  dataCenter?: string; // e.g., 'us', 'eu', etc.
  isEnabled?: boolean;
}

declare global {
  interface Window {
    rtbhEvents?: any[];
  }
}

export class RtbHouseProvider implements Provider {
  type = ProviderType.RTB_HOUSE;
  isEnabled: boolean;
  loggingEnabled = false; // 公开属性，支持ReportingCenter访问
  private initialized = false;
  private config: RtbHouseConfig;

  constructor(config: RtbHouseConfig) {
    this.config = config;
    this.isEnabled = config.isEnabled ?? true;
    this.loggingEnabled = env.NEXT_PUBLIC_ENV === 'local';
  }

  async init(): Promise<boolean> {
    try {
      if (typeof window === 'undefined') return false;
      if (this.initialized) return true;

      const { trackingId } = this.config;
      const dataCenter = this.config.dataCenter || 'us';

      // RTB House initialization script based on provided code
      return new Promise((resolve) => {
        const w = window;
        const d = document;
        const dn = 'rtbhEvents';
        const t = trackingId;

        // Initialize rtbhEvents array if not exists and push init event
        w[dn] = w[dn] || [];
        w[dn].push({
          eventType: 'init',
          value: t,
          dc: dataCenter,
        });

        // Create and inject the tracking script
        const f = d.getElementsByTagName('script')[0];
        const c = d.createElement('script') as HTMLScriptElement;
        c.async = true;
        c.src = `https://tags.creativecdn.com/${trackingId}.js`;

        c.onload = async () => {
          this.initialized = true;
          if (this.loggingEnabled) {
            logger.info('RTB House tracking initialized successfully');
          }
          // 初始化完成后等待权威 Device ID，再绑定设备标识。
          window.rtbhEvents.push({
            eventType: 'uid',
            id: await getOrCreateDeviceId(),
          });
          resolve(true);
        };

        c.onerror = (error) => {
          logger.error('Failed to load RTB House tracking script:', error);
          resolve(false);
        };

        if (f && f.parentNode) {
          f.parentNode.insertBefore(c, f);
        } else {
          // Fallback: append to head if no script tags exist
          document.head.appendChild(c);
        }
      });
    } catch (error) {
      logger.error('Failed to initialize RTB House tracking:', error);
      return false;
    }
  }

  /**
   * 接收既有调用方的业务事件；出口关闭时直接丢弃，避免业务事件与用户标识进入第三方队列。
   */
  track(event: TrackingEvent): void {
    // 保留 track 公共接口以兼容所有既有调用方，但在 Provider 内统一熔断数据发送。
    if (!RTB_HOUSE_BUSINESS_EVENTS_ENABLED) return;

    if (!this.initialized || typeof window === 'undefined') return;

    try {
      if (window.rtbhEvents && Array.isArray(window.rtbhEvents)) {
        if (this.loggingEnabled) {
          logger.info('RTB House tracking event:', event);
        }

        // Push event to RTB House events queue
        window.rtbhEvents.push(
          {
            eventType: event.eventName,
            ...event.properties,
          },
          {
            eventType: 'uid',
            id: localStorage.getItem('zl-userId') || 'unknown',
          },
        );
      }
    } catch (error) {
      logger.error('RTB House tracking error:', error);
    }
  }

  /**
   * 启用RTB House日志
   */
  enableLog(): void {
    this.loggingEnabled = true;
  }

  /**
   * 禁用RTB House日志
   */
  disableLog(): void {
    this.loggingEnabled = false;
  }

  isInitialized(): boolean {
    return this.initialized;
  }
}
